import { useEffect, useRef } from 'react';
import DailyIframe, { type DailyCall, type DailyParticipant } from '@daily-co/daily-js';

type Props = {
  conversationUrl: string;
  className?: string;
  onReady?: () => void;
};

let activeDailyCall: DailyCall | null = null;
let destroyPromise: Promise<void> | null = null;

/** @internal Visible only for tests — force-resets module state. */
export function _resetDailyStateForTesting(): void {
  activeDailyCall = null;
  destroyPromise = null;
}

function destroyCall(call: DailyCall): void {
  if (activeDailyCall === call) activeDailyCall = null;
  destroyPromise = call
    .leave()
    .then(() => call.destroy())
    .catch(() => call.destroy().catch(() => {}))
    .finally(() => {
      if (destroyPromise !== null) destroyPromise = null;
    });
}

function getPlayableTrack(p: DailyParticipant, kind: 'video' | 'audio'): MediaStreamTrack | null {
  const t = p.tracks?.[kind];
  if (t?.state === 'playable' && t.persistentTrack) return t.persistentTrack;
  return null;
}

/**
 * Joins the Tavus CVI Daily room via createCallObject (no Prebuilt UI),
 * extracts remote video+audio tracks, and renders them in raw elements.
 */
export function TavusVideoTile({ conversationUrl, className, onReady }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const readyFiredRef = useRef(false);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    if (!conversationUrl.trim()) return;

    let cancelled = false;
    let localCall: DailyCall | null = null;

    const attachRemoteTracks = (call: DailyCall) => {
      const participants = call.participants();
      for (const [id, p] of Object.entries(participants)) {
        if (id === 'local') continue;
        const vTrack = getPlayableTrack(p, 'video');
        if (vTrack && videoRef.current) {
          videoRef.current.srcObject = new MediaStream([vTrack]);
          console.debug('[TavusVideoTile] attached video track', { participantId: id });
          if (!readyFiredRef.current) {
            readyFiredRef.current = true;
            onReadyRef.current?.();
          }
        }
        const aTrack = getPlayableTrack(p, 'audio');
        if (aTrack && audioRef.current) {
          audioRef.current.srcObject = new MediaStream([aTrack]);
          console.debug('[TavusVideoTile] attached audio track', { participantId: id });
        }
      }
    };

    const setup = async () => {
      if (destroyPromise) await destroyPromise;
      if (cancelled || activeDailyCall) return;

      const call = DailyIframe.createCallObject({ videoSource: false });
      activeDailyCall = call;
      localCall = call;

      const onParticipantEvent = () => attachRemoteTracks(call);
      const onLeftMeeting = () => {
        console.warn('[TavusVideoTile] left meeting');
        destroyCall(call);
      };
      const onError = (evt?: unknown) => {
        console.warn('[TavusVideoTile] Daily error', evt);
        destroyCall(call);
      };

      call.on('participant-joined', onParticipantEvent);
      call.on('participant-updated', onParticipantEvent);
      call.on('participant-left', onParticipantEvent);
      call.on('left-meeting', onLeftMeeting);
      call.on('error', onError);

      try {
        await call.join({
          url: conversationUrl,
          userName: 'Viewer',
          startVideoOff: true,
        });
        if (!cancelled) {
          console.info('[TavusVideoTile] joined Daily room');
          attachRemoteTracks(call);
        }
      } catch (err) {
        console.warn('[TavusVideoTile] Daily join failed', err);
        destroyCall(call);
      }
    };

    void setup();

    return () => {
      cancelled = true;
      if (localCall) {
        destroyCall(localCall);
        localCall = null;
      }
    };
  }, [conversationUrl]);

  if (!conversationUrl.trim()) return null;

  return (
    <div className={className ?? 'w-full h-full min-h-0'}>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={false}
        className="w-full h-full object-cover"
      />
      {/* Hidden audio element carries the replica's voice */}
      <audio ref={audioRef} autoPlay playsInline />
    </div>
  );
}
